import { type ReactNode, useState } from 'react';
import { cn } from '@/nucleo/utilitarios';
import { usePrintMode } from './print/PrintMode';
import { Button } from '@/ui/base/button';
import { Monitor, Smartphone, Sun, Moon } from 'lucide-react';

type PreviewWidth = 'auto' | 'full' | 'narrow' | 'mobile';

interface LivePreviewProps {
  children: ReactNode;
  /** Largura do palco. */
  width?: PreviewWidth;
  /** Escala visual do conteúdo (usa zoom para preservar altura no fluxo). */
  scale?: number;
  /** Habilita alternância desktop/mobile. */
  responsive?: boolean;
  /** Habilita alternância claro/escuro no palco. */
  themeToggle?: boolean;
  /** Superfície de fundo do palco. */
  surface?: 'background' | 'card' | 'muted';
  className?: string;
}

const WIDTHS: Record<PreviewWidth, string> = {
  auto: 'w-auto',
  full: 'w-full',
  narrow: 'w-full max-w-xl',
  mobile: 'w-full max-w-[375px]',
};

const SURFACES = {
  background: 'bg-background',
  card: 'bg-card',
  muted: 'bg-muted/40',
};

/**
 * Palco isolado que renderiza componentes REAIS de produção dentro do admin.
 * Os providers globais (query, tema, rotas) já envolvem a página — não usar mocks.
 */
export function LivePreview({
  children,
  width = 'full',
  scale = 1,
  responsive = false,
  themeToggle = false,
  surface = 'background',
  className,
}: LivePreviewProps) {
  const printMode = usePrintMode();
  const [mobile, setMobile] = useState(false);
  const [dark, setDark] = useState(false);

  const effectiveWidth: PreviewWidth = printMode
    ? width === 'mobile'
      ? 'narrow'
      : width
    : mobile
      ? 'mobile'
      : width;
  const effectiveDark = printMode ? false : dark;
  const showToolbar = !printMode && (responsive || themeToggle);

  return (
    <div className={cn('rounded-xl border border-border overflow-hidden', className)}>
      {showToolbar && (
        <div className="flex items-center justify-end gap-1 px-2 py-1.5 border-b border-border bg-muted/30">
          {responsive && (
            <>
              <Button
                type="button"
                variant={mobile ? 'ghost' : 'secondary'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setMobile(false)}
                aria-pressed={!mobile}
                aria-label="Visualizar em desktop"
              >
                <Monitor className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant={mobile ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setMobile(true)}
                aria-pressed={mobile}
                aria-label="Visualizar em mobile"
              >
                <Smartphone className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
          {themeToggle && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => setDark((d) => !d)}
              aria-pressed={dark}
              aria-label="Alternar tema do palco"
            >
              {dark ? <Moon className="h-3.5 w-3.5" /> : <Sun className="h-3.5 w-3.5" />}
            </Button>
          )}
        </div>
      )}

      <div
        className={cn(effectiveDark && 'dark', SURFACES[surface], 'p-5 flex justify-center')}
        // Cliques em links neutralizados: o usuário não deve sair da documentação.
        onClickCapture={(e) => {
          const target = (e.target as HTMLElement).closest('a');
          if (target) e.preventDefault();
        }}
      >
        <div
          className={cn(WIDTHS[effectiveWidth])}
          style={scale !== 1 ? { zoom: scale } : undefined}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
